import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { EconomicCalendar } from "@/components/ui/economic-calendar";
import { CircleNotch, ArrowLeft, CheckCircle, XCircle, WarningCircle, Question, ArrowUp, ArrowDown, Newspaper, Info, Clock, ArrowsClockwise } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { label: "1W", value: "5d", interval: "1h" },
  { label: "1M", value: "1mo", interval: "1d" },
  { label: "3M", value: "3mo", interval: "1d" },
  { label: "6M", value: "6mo", interval: "1d" },
  { label: "1Y", value: "1y", interval: "1d" },
];

const VERDICT_STYLES = {
  compliant: "bg-green-50 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
  borderline: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  non_compliant: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
};
const VERDICT_LABELS = { compliant: "COMPLIANT", borderline: "BORDERLINE", non_compliant: "NON-COMPLIANT" };

function StatusIcon({ status }) {
  if (status === "pass") return <CheckCircle size={16} weight="fill" className="text-green-600 dark:text-green-400 shrink-0" />;
  if (status === "borderline") return <WarningCircle size={16} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0" />;
  if (status === "unavailable") return <Question size={16} weight="fill" className="text-zinc-400 dark:text-zinc-500 shrink-0" />;
  return <XCircle size={16} weight="fill" className="text-red-600 dark:text-red-400 shrink-0" />;
}

function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-xs font-mono">
      <p className="text-zinc-400 dark:text-zinc-500 mb-1">{d.date}</p>
      <p className="text-zinc-950 dark:text-zinc-100 font-medium">${d.close?.toFixed(2)}</p>
    </div>
  );
}

function InlineStockDetail({ ticker, onBack }) {
  const [stock, setStock] = useState(null);
  const [quote, setQuote] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [news, setNews] = useState([]);
  const [period, setPeriod] = useState("1mo");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lookupRes, quoteRes, newsRes] = await Promise.all([
        axios.get(`${API}/stocks/lookup/${ticker}`),
        axios.get(`${API}/stocks/${ticker}/quote`).catch(() => ({ data: null })),
        axios.get(`${API}/stocks/${ticker}/news`).catch(() => ({ data: { news: [] } })),
      ]);
      setStock(lookupRes.data);
      setQuote(quoteRes.data);
      setNews(newsRes.data?.news || []);
    } catch { /* */ }
    setLoading(false);
  }, [ticker]);

  const fetchChart = useCallback(async (p) => {
    setChartLoading(true);
    const pObj = PERIODS.find((x) => x.value === p) || PERIODS[1];
    try {
      const res = await axios.get(`${API}/stocks/${ticker}/chart`, { params: { period: pObj.value, interval: pObj.interval } });
      setChartData(res.data.data || []);
    } catch { setChartData([]); }
    setChartLoading(false);
  }, [ticker]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchChart(period); }, [period, fetchChart]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <CircleNotch size={24} weight="bold" className="animate-spin text-zinc-400" />
        <span className="ml-3 text-xs font-mono text-zinc-500">Loading {ticker}...</span>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="py-8">
        <button onClick={onBack} className="flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-zinc-200 mb-4 transition-colors" data-testid="calendar-back-btn">
          <ArrowLeft size={14} weight="bold" /> Back to Calendar
        </button>
        <p className="text-sm font-mono text-zinc-500">Could not load data for {ticker}.</p>
      </div>
    );
  }

  const change = quote?.change || 0;
  const changePct = quote?.change_percent || 0;
  const isPositive = change >= 0;
  const color = stock.verdict === "non_compliant" ? "#f97316" : stock.verdict === "borderline" ? "#f59e0b" : "#0ea5e9";

  const ratios = [
    { label: "Debt / Market Cap", value: stock.debt_ratio, status: stock.debt_status, threshold: "< 33%", max: 33 },
    { label: "Interest / Revenue", value: stock.interest_ratio, status: stock.interest_status, threshold: "< 5%", max: 5 },
    { label: "Receivables / Assets", value: stock.receivable_ratio, status: stock.receivable_status, threshold: "< 49%", max: 49 },
  ];

  return (
    <div className="mt-4" data-testid="calendar-stock-detail">
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-zinc-200 mb-4 transition-colors" data-testid="calendar-back-btn">
        <ArrowLeft size={14} weight="bold" /> Back to Calendar
      </button>

      {/* Header */}
      <div className="border border-zinc-200 dark:border-zinc-800 p-5 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-mono font-medium text-zinc-950 dark:text-zinc-100">{stock.company_name}</h2>
            <p className="text-[11px] font-mono text-zinc-500">{stock.ticker} · {stock.sector}</p>
          </div>
          <div className="flex items-center gap-4">
            {quote && (
              <div className="text-right">
                <p className="text-xl font-mono font-light text-zinc-950 dark:text-zinc-100">${quote.price?.toFixed(2)}</p>
                <p className={`text-xs font-mono flex items-center gap-1 justify-end ${isPositive ? "text-green-500" : "text-red-500"}`}>
                  {isPositive ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
                  {isPositive ? "+" : ""}{change.toFixed(2)} ({changePct.toFixed(2)}%)
                </p>
              </div>
            )}
            <span className={`inline-flex px-3 py-1 text-[10px] font-mono font-bold uppercase border tracking-widest ${VERDICT_STYLES[stock.verdict] || VERDICT_STYLES.non_compliant}`}>
              {VERDICT_LABELS[stock.verdict] || "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Ratios + Explanation */}
        <div className="border border-zinc-200 dark:border-zinc-800 p-5">
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4">AAOIFI Compliance Ratios</p>
          <div className="space-y-4">
            {ratios.map((r) => (
              <div key={r.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-mono text-zinc-500 dark:text-zinc-400">{r.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-zinc-800 dark:text-zinc-200">{r.value}%</span>
                    <span className="text-[9px] font-mono text-zinc-400">/ {r.threshold}</span>
                    <StatusIcon status={r.status} />
                  </div>
                </div>
                <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-700 ${r.status === "pass" ? "bg-green-500" : r.status === "borderline" ? "bg-amber-500" : "bg-red-500"}`}
                    style={{ width: `${Math.min(100, (r.value / r.max) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {stock.explanation && (
            <div className="mt-5 pt-4 border-t border-zinc-100 dark:border-zinc-800">
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-2 flex items-center gap-1.5">
                <Info size={10} weight="bold" /> Why This Classification
              </p>
              <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">{stock.explanation}</p>
            </div>
          )}

          {/* Source badges */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-800/40 bg-emerald-950/30 text-[9px] font-mono tracking-wide text-emerald-400/80">
              <span className="w-1 h-1 rounded-full bg-emerald-500/60 shrink-0" />
              Ratios: {stock.source === "fmp+yfinance" ? "FMP + YF" : "FMP"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-sky-800/40 bg-sky-950/30 text-[9px] font-mono tracking-wide text-sky-400/80">
              <span className="w-1 h-1 rounded-full bg-sky-500/60 shrink-0" />
              Price & News: Yahoo Finance
            </span>
          </div>
        </div>

        {/* Right: Chart */}
        <div className="border border-zinc-200 dark:border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">Price Chart</p>
            <div className="flex gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-2 py-1 text-[10px] font-mono transition-colors ${
                    period === p.value
                      ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900"
                      : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 220 }}>
            {chartLoading ? (
              <div className="h-full flex items-center justify-center">
                <CircleNotch size={20} className="animate-spin text-zinc-400" />
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-xs font-mono text-zinc-500">No chart data</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="calChartGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.08} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "monospace", fill: "#71717a" }} stroke="transparent" tickLine={false} interval="preserveStartEnd" minTickGap={50} />
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 9, fontFamily: "monospace", fill: "#71717a" }} stroke="transparent" tickLine={false} axisLine={false} width={50} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="close" stroke={color} fill="url(#calChartGrad)" strokeWidth={1.5} dot={false} activeDot={{ r: 3, stroke: color, fill: "#09090b", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* News */}
      <div className="border border-zinc-200 dark:border-zinc-800 p-5 mt-4">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3 flex items-center gap-1.5">
          <Newspaper size={12} weight="bold" /> Latest News
        </p>
        {news.length === 0 ? (
          <p className="text-xs font-mono text-zinc-500">No news available.</p>
        ) : (
          <div className="space-y-0">
            {news.slice(0, 5).map((a, i) => (
              <a
                key={i}
                href={a.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block py-2.5 border-b border-zinc-100 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition-colors -mx-2 px-2"
              >
                <p className="text-sm text-zinc-800 dark:text-zinc-300 line-clamp-1">{a.title}</p>
                <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-600 mt-0.5">{a.publisher}</p>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EconomicCalendarView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [complianceMap, setComplianceMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/economic-calendar`);
        if (!cancelled) {
          const evts = res.data || [];
          setEvents(evts);
          // Fetch compliance for tickers that have them
          const tickers = [...new Set(evts.map(e => e.ticker).filter(Boolean))];
          if (tickers.length > 0) {
            try {
              const stocksRes = await axios.get(`${API}/stocks`);
              const map = {};
              (stocksRes.data || []).forEach(s => { map[s.ticker] = s.verdict; });
              if (!cancelled) setComplianceMap(map);
            } catch { /* ignore */ }
          }
        }
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvents();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="calendar-loading">
        <CircleNotch size={24} weight="bold" className="animate-spin text-zinc-400" />
        <span className="ml-3 text-xs font-mono text-zinc-500">Loading economic calendar...</span>
      </div>
    );
  }

  // Show inline stock detail when a ticker is selected
  if (selectedTicker) {
    return <InlineStockDetail ticker={selectedTicker} onBack={() => setSelectedTicker(null)} />;
  }

  // Enrich events with compliance status
  const enrichedEvents = events.map(e => ({
    ...e,
    verdict: e.ticker ? complianceMap[e.ticker] : undefined,
  }));

  return (
    <div className="mt-6" data-testid="economic-calendar-view">
      <EconomicCalendar
        title="Upcoming Earnings & Events"
        events={enrichedEvents}
        onEventClick={(ticker) => setSelectedTicker(ticker)}
      />

      {/* Compliance context note */}
      {events.length > 0 && (
        <div className="mt-3 px-3 py-2 border border-amber-200 dark:border-amber-900 bg-amber-50/30 dark:bg-amber-950/10">
          <p className="text-[10px] font-mono text-amber-700 dark:text-amber-400">
            Earnings may affect financial ratios and compliance status. Monitor holdings closely around earnings dates.
          </p>
        </div>
      )}

      {events.length > 0 && (
        <div className="mt-4 border border-zinc-200 dark:border-zinc-800 p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3">
            Upcoming Earnings Summary
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <p className="text-lg font-mono font-light text-zinc-900 dark:text-zinc-100">{events.length}</p>
              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider">Total Events</p>
            </div>
            <div>
              <p className="text-lg font-mono font-light text-red-400">
                {events.filter((e) => e.impact === "high").length}
              </p>
              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider">High Impact</p>
            </div>
            <div>
              <p className="text-lg font-mono font-light text-zinc-900 dark:text-zinc-100">
                {new Set(events.map((e) => e.date)).size}
              </p>
              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider">Days Covered</p>
            </div>
            <div>
              <p className="text-lg font-mono font-light text-zinc-900 dark:text-zinc-100">
                {new Set(events.map((e) => e.countryCode)).size}
              </p>
              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider">Countries</p>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] font-mono text-zinc-400 dark:text-zinc-600 mt-3">
        Earnings data sourced from Yahoo Finance. Click any card to view the stock's Shariah compliance.
      </p>
    </div>
  );
}
